import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import BasicDetails from "./sections/BasicDetails";
import DepositoryDetails from "./sections/DepositoryDetails";
import BankDetails from "./sections/BankDetails";
import TradingSegments from "./sections/TradingSegments";
import NomineeDetails from "./sections/NomineeDetails";
import Icon from "@/components/units/Icon";

import { loadHyperverge } from "@/utility/hyperverge";

import { useNotification } from "@/libraries/notification/NotificationContext"
import { NOTIFICATION } from "@/constants"
import { logoutUser } from "@/services/rest/user-logout";
import { ModalPortal } from "@/components/units/Modal/ModalPortal";
import Modal from "@/components/units/Modal/Modal";
import { formatDate } from "@/utility/formatter";
import Button from "@/components/units/button/Button";
import { getTechExcelProfile } from "@/services/rest/techexcel-profile";
import { setTechExcelDataAction, setErrorAction } from "@/store/techExcel/techExcel";

const updateRestrictions = {
  emailUpdate: "emailModifications",
  phoneUpdate: "phoneModifications",
  bankUpdate:"bankAccountModifications",
  segmentUpdate: "segmentModifications"
};


const MODIFICATION_BUFFER_TIME = 7

enum HYPERVERGE_STATUS {
  APPROVED = "approve",
  REVIEW = "review",
  REJECTED = "reject",
  CANCELLED = "cancel",
  ERROR = "error"
}


const Profile = () => {
  const navigate = useNavigate();
  const { sendNotification:sendToast } = useNotification(NOTIFICATION.TOAST);

  const isLoading = useSelector((state: any) => state.user.isLoading);
  const hasError = useSelector((state: any) => state.user.error);
  const userData = useSelector((state: any) => state.user.user);
  const isEnabledBankModification = useSelector((state:any) => state.featureflag?.hyperverge?.modification?.bank?.isEnabled)
  const forcedLogoutPhoneModification = useSelector((state:any) => state.featureflag?.hyperverge?.modification?.phone?.forcedLogout)

  const techExcelData = useSelector((state:any) => state?.techExcel)

  const [showModal,setShowModal] = useState(false)
  const domNode = document.getElementById("modal-container")

  const modalRef = useRef(null)

  const [openItem, setOpenItem] = useState("basic-details");
  const [modalType,setModalType] = useState(null)
  const isMobile = useSelector((state: any) => state.global.device.below.sm);

  const dispatch = useDispatch()

  const toggleAccordion = (e: React.MouseEvent<HTMLDivElement>) => {
    const id = e.currentTarget.id;

    if(id == "close-account"){
      sendToast("Please download the app to proceed with account closure.")
    }
    setOpenItem(openItem === id ? "" : id);
  };

  const handleModificationFailure = (error) => {
    if(error.message){
      if (error.message.includes('last updated')) {
        sendToast(error.message)
      }
    }
    else{
      switch (error.status) {
        // ----Complete workflow-----
        case "auto_declined":
          sendToast("Profile updation declined")
          break;
        case "user_cancelled":
          sendToast("Profile updation cancelled")
          break;
        case "needs_review":
            // ----Incomplete workflow-----
            sendToast("Profile updation under review")
            break;
        case "error":
        default:
          sendToast("Error updating the profile")
      }
    }
  }


  const handleModificationSucess = (res) => {
    // reload
    const modificationType = res.transactionId.split("-")[0]

    switch(modificationType){
      case "emailmodification":
      case "phonemodification":
        if(forcedLogoutPhoneModification){
          setTimeout(() => {
            logoutUser()
          }, 3000);
        }
      case "bankaccountmodification":
      case "segmentmodification":
        setShowModal(true)
        break;
      default:
        sendToast("Profile updated succesfully")

      navigate(".",{
        replace: true
      })
    }
  }

  const updateProfileData = async (workFlow)=>{

    if(userData){

      if (updateRestrictions[workFlow]) {
        const modificationsKey = updateRestrictions[workFlow];
        const modifications = userData[modificationsKey]?.filter(mod => {
          const status = mod?.hyperverge_application_status?.toLowerCase()
          return status?.includes(HYPERVERGE_STATUS.APPROVED) || status?.includes(HYPERVERGE_STATUS.REVIEW)
        });
        const lastModificationTime = modifications?.[modifications.length - 1]?.applicationDate;
        
        if (lastModificationTime) {
          const lastModificationDate = new Date(lastModificationTime)
          const currentDate = new Date()
          const diffDays = Math.ceil(Math.abs(currentDate.getTime() - lastModificationDate.getTime()) / (1000 * 60 * 60 * 24));
          if (diffDays < MODIFICATION_BUFFER_TIME) {
            const upcomingDate = new Date(lastModificationDate);
            upcomingDate.setDate(lastModificationDate.getDate() + 7);
          
            sendToast(`Details changed on ${formatDate(lastModificationDate)}, further update allowed on ${formatDate(upcomingDate)}.`);
            return;
          }
        }
      }

      loadHyperverge(workFlow)
      .then(handleModificationSucess)
      .catch(handleModificationFailure)
    }
  }

  useEffect(() => {
    getTechExcelProfile().then(data => {
      dispatch(setTechExcelDataAction(data))
    }).catch(error => {
      dispatch(setErrorAction(error?.message))
    })
  },[])

  return (
    <div className="tw-w-full">
      {domNode &&
        <ModalPortal domNode={domNode}>
          <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
              <div
                className="tw-bg-white tw-p-6 tw-rounded-lg tw-text-center"
                ref={modalRef}
              >
              <p className="tw-text-primaryBlack tw-font-medium tw-text-[18px]">
                we are processing your request will get updated shortly
              </p>
              { 
                forcedLogoutPhoneModification &&
                <p className="tw-text-gray-600 tw-text-[14px] tw-mt-2">
                  You will be logged out now. Please log in with your new number.
                </p>
              }
              
              <button className="tw-bg-[#75FFFF] tw-rounded-md tw-w-full tw-max-w-[300px] tw-py-3 tw-mt-5" onClick={() => setShowModal(false)}>OK</button>
              </div>
          </Modal>
        </ModalPortal>
      }
      <div
        className="tw-bg-white tw-rounded-[6px] tw-p-4 tw-mb-4"
        style={{ boxShadow: "0px 0px 11px -2px #A7A7A71A" }}
      >
        <div
          id="basic-details"
          className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer"
          onClick={toggleAccordion}
        >
          <div className="tw-text-[16px] tw-text-primaryBlack tw-font-nmmed">
            Basic Details
          </div>
          <div>
            {openItem === "basic-details" ? (
              <Icon
                onClick={() => {}}
                iconName={"up-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            ) : (
              <Icon
                onClick={() => {}}
                iconName={"down-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            )}
          </div>
        </div>
        <div
          className={`${
            openItem === "basic-details" ? "tw-block" : "tw-hidden"
          }`}
        >
          {isLoading && (
            <div className="tw-text-center tw-py-8">Loading...</div>
          )}
          {!isLoading && hasError && (
            <div className="tw-text-center tw-py-8">
              Something went wrong. Please try again later.
            </div>
          )}
          {!isLoading && !hasError && Object.keys(userData).length === 0 && (
            <div className="tw-text-center tw-py-8">No data found</div>
          )}
          {!isLoading && !hasError && Object.keys(userData).length > 0 && (
            <BasicDetails data={userData} onProfileUpdate={updateProfileData} />
          )}
        </div>
      </div>
      <div
        className="tw-bg-white tw-rounded-[6px] tw-p-4 tw-mb-4"
        style={{ boxShadow: "0px 0px 11px -2px #A7A7A71A" }}
      >
        <div
          id="depository-details"
          className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer"
          onClick={toggleAccordion}
        >
          <div className="tw-text-[16px] tw-text-primaryBlack tw-font-nmmed">
            Depository Details
          </div>
          <div>
            {openItem === "depository-details" ? (
              <Icon
                onClick={() => {}}
                iconName={"up-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            ) : (
              <Icon
                onClick={() => {}}
                iconName={"down-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            )}
          </div>
        </div>
        <div
          className={`${
            openItem === "depository-details" ? "tw-block" : "tw-hidden"
          }`}
        >
          {isLoading && (
            <div className="tw-text-center tw-py-8">Loading...</div>
          )}
          {!isLoading && hasError && (
            <div className="tw-text-center tw-py-8">
              Something went wrong. Please try again later.
            </div>
          )}
          {!isLoading && !hasError && Object.keys(userData).length === 0 && (
            <div className="tw-text-center tw-py-8">No data found</div>
          )}
          {!isLoading && !hasError && Object.keys(userData).length > 0 && (
            <DepositoryDetails data={userData} />
          )}
        </div>
      </div>
      <div
        className="tw-bg-white tw-rounded-[6px] tw-p-4 tw-mb-4"
        style={{ boxShadow: "0px 0px 11px -2px #A7A7A71A" }}
      >
        <div
          id="bank-details"
          className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer"
          onClick={toggleAccordion}
        >
          <div className="tw-text-[16px] tw-text-primaryBlack tw-font-nmmed">
            Bank Details
          </div>
          <div>
            {openItem === "bank-details" ? (
              <Icon
                onClick={() => {}}
                iconName={"up-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            ) : (
              <Icon
                onClick={() => {}}
                iconName={"down-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            )}
          </div>
        </div>
        <div
          className={`${
            openItem === "bank-details" ? "tw-block" : "tw-hidden"
          }`}
        >
          {techExcelData?.isLoading && (
            <div className="tw-text-center tw-py-8">Loading...</div>
          )}
          {!techExcelData?.isLoading && techExcelData?.hasError && (
            <div className="tw-text-center tw-py-8">
              Something went wrong. Please try again later.
            </div>
          )}
          {!techExcelData?.isLoading &&
            !techExcelData?.hasError &&
            (Object.keys(techExcelData).length === 0 ||
              techExcelData?.bankAccounts?.length === 0) && (
              <div className="tw-text-center tw-py-8">No data found</div>
            )}
          {!techExcelData?.isLoading && !techExcelData?.hasError && Object.keys(userData).length > 0 && (
            <div>
              {techExcelData?.bankAccounts?.map((bank: any) => (
                <BankDetails data={bank} key={bank.bankAccountNumber} />
              ))}
              {isEnabledBankModification && techExcelData?.bankAccounts?.length < 3 &&
              <div className="tw-cursor-pointer tw-flex tw-items-center tw-gap-1">
                <div>
                  <Icon
                    onClick={() => {}}
                    iconName={"add-icon"}
                    wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
                  >
                    <></>
                  </Icon>
                </div>
                <button onClick={() => updateProfileData("bankUpdate")} className="tw-text-[12px] tw-text-primaryBlack tw-font-nmmed">
                  Update Bank Account
                </button>
              </div>
              }
            </div>
          )}
        </div>
      </div>
      <div
        className="tw-bg-white tw-rounded-[6px] tw-p-4 tw-mb-4"
        style={{ boxShadow: "0px 0px 11px -2px #A7A7A71A" }}
      >
        <div
          id="trading-segments"
          className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer"
          onClick={toggleAccordion}
        >
          <div className="tw-text-[16px] tw-text-primaryBlack tw-font-nmmed">
            Trading Segments
          </div>
          <div>
            {openItem === "trading-segments" ? (
              <Icon
                onClick={() => {}}
                iconName={"up-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            ) : (
              <Icon
                onClick={() => {}}
                iconName={"down-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            )}
          </div>
        </div>
        <div
          className={`${
            openItem === "trading-segments" ? "tw-block" : "tw-hidden"
          }`}
        >
          <TradingSegments data={userData} onProfileUpdate={updateProfileData} />
        </div>
      </div>
      
      {(techExcelData?.nominees === null || techExcelData?.nominees?.length > 0) &&
      <div
        className="tw-bg-white tw-rounded-[6px] tw-p-4 tw-mb-4"
        style={{ boxShadow: "0px 0px 11px -2px #A7A7A71A" }}
      >
        <div
          id="nominee-details"
          className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer"
          onClick={toggleAccordion}
        >
          <div className="tw-text-[16px] tw-text-primaryBlack tw-font-nmmed">
            Nominee Details
          </div>
          <div>
            {openItem === "nominee-details" ? (
              <Icon
                onClick={() => {}}
                iconName={"up-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            ) : (
              <Icon
                onClick={() => {}}
                iconName={"down-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            )}
          </div>
        </div>
        <div
          className={`${
            openItem === "nominee-details" ? "tw-block" : "tw-hidden"
          }`}
        >
          {techExcelData?.isLoading && (
            <div className="tw-text-center tw-py-8">Loading...</div>
          )}
          {!techExcelData?.isLoading && techExcelData?.hasError && (
            <div className="tw-text-center tw-py-8">
              Something went wrong. Please try again later.
            </div>
          )}
          {!techExcelData?.isLoading &&
            !techExcelData?.hasError &&
            (Object.keys(techExcelData)?.length === 0 ||
              techExcelData?.nominees?.length === 0) && (
              <div className="tw-text-center tw-py-8">No data found</div>
            )}
          {!techExcelData?.isLoading && !techExcelData?.hasError && Object.keys(techExcelData)?.length > 0 && (
            <div className="tw-mt-3">
              {techExcelData?.nominees?.map((nominee: any,index:number) => (
                <>
                {
                  index < techExcelData.nominees.length &&
                  <hr />
                }
                <NomineeDetails data={nominee} key={nominee.nomineeName} id={index}/>
                </>
              ))}
              {/*
                TODO: Nominee updation not yet supported
              <div className="tw-cursor-pointer tw-flex tw-items-center tw-gap-1">
                <div>
                  <Icon
                    onClick={() => {}}
                    iconName={"add-icon"}
                    wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
                  >
                    <></>
                  </Icon>
                </div>
                <div className="tw-text-[12px] tw-text-primaryBlack tw-font-nmmed">
                  Add New Nominee
                </div>
              </div> */}
            </div>
          )}
        </div>
      </div>
      }
      <div
        className="tw-bg-white tw-rounded-[6px] tw-p-4 tw-mb-4"
        style={{ boxShadow: "0px 0px 11px -2px #A7A7A71A" }}
      >
        <div
          id="close-account"
          className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer"
          onClick={toggleAccordion}
        >
          <div className="tw-text-[16px] tw-text-primaryBlack tw-font-nmmed">
            Close Account
          </div>
          <div>
            {openItem === "close-account-disabled" ? (
              <Icon
                onClick={() => {}}
                iconName={"up-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            ) : (
              <Icon
                onClick={() => {}}
                iconName={"down-icon"}
                wrapperStyle="tw-flex-grow-0 tw-box-content tw-text-primaryBlack"
              >
                <></>
              </Icon>
            )}
          </div>
        </div>
        <div
          className={`${
            openItem === "close-account" ? "tw-block" : "tw-hidden"
          }`}
        >
          <></>
        </div>
      </div>
    </div>
  );
};


const UpdatationResponseModal = ({domNode,showModal,setShowModal,modalRef,heading,text}) => {

  <ModalPortal domNode={domNode}>
  <Modal isOpen={showModal} onClose={() => setShowModal(false)} >
      <div
        className="tw-bg-white tw-p-6 tw-rounded-lg tw-text-center"
        ref={modalRef}
      >
      <p className="tw-text-primaryBlack tw-font-medium tw-text-[16px]">
        {heading}
      </p>
      <p className="tw-text-gray-600 tw-text-[14px] tw-mt-2">
        {text}
      </p>
      </div>
  </Modal>
</ModalPortal>
}

export default Profile;
